import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { showToast, Skeleton } from '@mercai/clever'
import {
  GeneralInformation,
  ProfileDirections,
  ProfileDocuments,
  ProfileInfosHeader,
  ProfilePaymentPreferences,
  ProfileProductsIndustry,
  ProfileQuestionsDrawer,
  ProfileTaxInformation,
  ProductsIndustryDrawer,
  ProfileConsumption,
  ProfileConsumptionDrawer,
  ProfileLogisticPreferences,
  LogisticPreferencesDrawer,
  BuyerInformationDrawer,
  LegalRepresentativeDrawer,
  AddressDrawer,
  TaxInformationDrawer,
  PaymentRepresentativeDrawer,
  // ProfileSystemPreferences,
} from './components'
import { ProfilePaymentForm } from '@/components'
import {
  useAccountLevelQuestionList,
  useLeadUpdate,
  useUpdateProfile,
} from '@/services'

import { ProfileShowGeneralProps } from './general.type'
import {
  AccountLevelQuestionCategoryProps,
  LeadProps,
  ProfileProps,
} from '@/types'

import styles from './general.module.css'

const ProfileShowGeneralDefault = ({
  profile,
  setIsOpenGeneralInfosForm,
  profileRefetch,
}: ProfileShowGeneralProps) => {
  const { t } = useTranslation('screenProfileShow')

  const { mutateAsync: mutateAsyncProfile, isPending: isPendingProfile } =
    useUpdateProfile()

  const [isOpenAddressDrawer, setIsOpenAddressDrawer] = useState(false)

  const [isOpenLegalRepresentativeDrawer, setIsOpenLegalRepresentativeDrawer] =
    useState(false)

  const [
    isOpenPaymentRepresentativeDrawer,
    setIsOpenPaymentRepresentativeDrawer,
  ] = useState(false)

  const [isOpenGeneralInformationDrawer, setIsOpenGeneralInformationDrawer] =
    useState(false)

  const [isOpenPaymentForm, setIsOpenPaymentForm] = useState(false)

  const [isOpenProductsIndustryDrawer, setIsOpenProductsIndustryDrawer] =
    useState(false)

  const [isOpenConsumptionDrawer, setIsOpenConsumptionDrawer] = useState(false)

  const [isOpenLogisticPreferencesDrawer, setIsOpenLogisticPreferencesDrawer] =
    useState(false)

  const [isOpenTaxInformationDrawer, setIsOpenTaxInformationDrawer] =
    useState(false)

  const [categorySelected, setCategorySelected] = useState<
    AccountLevelQuestionCategoryProps | undefined
  >(undefined)

  const { data: dataQuestionList, isLoading: isLoadingQuestionList } =
    useAccountLevelQuestionList()

  const { mutateAsync: mutateAsyncLead, isPending: isPendingLead } =
    useLeadUpdate(profile?.leads?.[0]?.uuid || '')

  const handleLeadUpdate = async (values: Partial<LeadProps>) => {
    const response = await mutateAsyncLead(values)

    if (response.success) {
      profileRefetch()

      showToast({
        variant: 'success',
        title: t('generalViewLeadUpdateSucessTitle'),
      })

      setIsOpenProductsIndustryDrawer(false)
      setIsOpenConsumptionDrawer(false)

      return
    }

    showToast({
      variant: 'error',
      title: t('generalViewLeadUpdateErrorTitle'),
    })
  }

  const handleProfileUpdate = async (values: Partial<ProfileProps>) => {
    const response = await mutateAsyncProfile({
      ...values,
      uuid: profile?.uuid,
    })

    if (response.success) {
      profileRefetch()

      showToast({
        variant: 'success',
        title: t('generalViewLeadUpdateSucessTitle'),
      })

      setIsOpenLogisticPreferencesDrawer(false)

      return
    }

    showToast({
      variant: 'error',
      title: t('generalViewLeadUpdateErrorTitle'),
    })
  }

  const getQuestionsByCategory = () => {
    return (
      dataQuestionList?.data?.results?.filter(
        (question) => question.category.slug === categorySelected?.slug,
      ) || []
    )
  }

  const onUnselectCategory = () => {
    setCategorySelected(undefined)
  }

  return (
    <>
      <div className={styles.core}>
        <ProfileInfosHeader
          profile={profile}
          onClickEdit={() => setIsOpenGeneralInfosForm(true)}
        />

        <div className={styles.content}>
          <div>
            <GeneralInformation
              profile={profile}
              onClickEditBuyer={() => setIsOpenGeneralInformationDrawer(true)}
              onClickEditLegalRepresentative={() =>
                setIsOpenLegalRepresentativeDrawer(true)
              }
              onClickEditPaymentRepresentative={() =>
                setIsOpenPaymentRepresentativeDrawer(true)
              }
            />

            <ProfileTaxInformation
              profile={profile}
              onSelectCategory={() => setIsOpenTaxInformationDrawer(true)}
            />

            <ProfilePaymentPreferences
              profile={profile}
              onClickEdit={() => setIsOpenPaymentForm(true)}
            />

            <ProfileDirections
              profile={profile}
              onClickEdit={() => setIsOpenAddressDrawer(true)}
            />

            <ProfileProductsIndustry
              lead={profile?.leads?.[0]}
              onClickEdit={() => setIsOpenProductsIndustryDrawer(true)}
            />

            <ProfileConsumption
              lead={profile?.leads?.[0]}
              onClickEdit={() => setIsOpenConsumptionDrawer(true)}
            />

            <ProfileLogisticPreferences
              profile={profile}
              onClickEdit={() => setIsOpenLogisticPreferencesDrawer(true)}
            />

            {/* 
            WE WILL ADD THIS PART IN THE FUTURE
            <ProfileSystemPreferences
              profile={profile}
              onClickEdit={() => setIsOpenGeneralInfosForm(true)}
            /> */}
          </div>

          <ProfileDocuments profile={profile} />
        </div>
      </div>

      <BuyerInformationDrawer
        isOpen={isOpenGeneralInformationDrawer}
        onHandleOpen={() => setIsOpenGeneralInformationDrawer(false)}
        profileUuid={profile?.uuid || ''}
      />

      <LegalRepresentativeDrawer
        isOpen={isOpenLegalRepresentativeDrawer}
        onHandleOpen={() => setIsOpenLegalRepresentativeDrawer(false)}
        profile={profile}
      />

      <PaymentRepresentativeDrawer
        isOpen={isOpenPaymentRepresentativeDrawer}
        onHandleOpen={() => setIsOpenPaymentRepresentativeDrawer(false)}
        profile={profile}
      />

      <AddressDrawer
        isOpen={isOpenAddressDrawer}
        onHandleOpen={() => setIsOpenAddressDrawer(false)}
        profileUuid={profile?.uuid || ''}
      />

      <ProfileQuestionsDrawer
        profile={profile}
        isLoading={isLoadingQuestionList}
        questions={getQuestionsByCategory()}
        categorySelected={categorySelected}
        onUnselectCategory={onUnselectCategory}
      />

      <ProfilePaymentForm
        isOpen={isOpenPaymentForm}
        onHandleOpen={() => setIsOpenPaymentForm(false)}
        profileUuid={profile?.uuid || ''}
        profileName={profile?.name || ''}
        financeProfileData={profile?.financeProfile}
        financeProfileRefetch={profileRefetch}
      />

      <ProductsIndustryDrawer
        isOpen={isOpenProductsIndustryDrawer}
        onHandleOpen={() => setIsOpenProductsIndustryDrawer(false)}
        values={profile?.leads?.[0]}
        onSubmit={handleLeadUpdate}
        isLoading={isPendingLead}
      />

      <ProfileConsumptionDrawer
        isOpen={isOpenConsumptionDrawer}
        onHandleOpen={() => setIsOpenConsumptionDrawer(false)}
        values={profile?.leads?.[0]}
        onSubmit={handleLeadUpdate}
        isLoading={isPendingLead}
      />

      <LogisticPreferencesDrawer
        isOpen={isOpenLogisticPreferencesDrawer}
        onHandleOpen={() => setIsOpenLogisticPreferencesDrawer(false)}
        values={profile}
        onSubmit={handleProfileUpdate}
        isLoading={isPendingProfile}
      />

      <TaxInformationDrawer
        isOpen={isOpenTaxInformationDrawer}
        profile={profile}
        onHandleOpen={() => setIsOpenTaxInformationDrawer(false)}
      />
    </>
  )
}

const ProfileShowGeneralLoading = () => (
  <div className={styles.core}>
    <Skeleton height="6rem" width="100%" />

    <div className={styles.content}>
      <div>
        <Skeleton height="8rem" width="100%" />

        <Skeleton height="8rem" width="100%" />

        <Skeleton height="8rem" width="100%" />

        <Skeleton height="8rem" width="100%" />
      </div>

      <Skeleton height="40rem" width="50%" />
    </div>
  </div>
)

export const ProfileShowGeneral = (props: ProfileShowGeneralProps) => {
  if (props.isLoading) {
    return <ProfileShowGeneralLoading />
  }

  return <ProfileShowGeneralDefault {...props} />
}
